import setMultiselectOptions from 'utils/DOM/setMultiselectOptions'
import { numberToCurrency, propAccessor } from '@hc-frontend/utils'
import getPeriodOptions from './getPeriodOptions'
import getDeductibleOptions from './getDeductibleOptions'
import toggleClass from 'utils/DOM/toggleClass'

export const filterBenefits = {
  deductible: 'deductible',
  coverage: 'lifetimeMax',
  coinsurance: 'userCoinsurance',
  moop: 'outOfPocketMax'
}

function renderPlanFilters(
  plansToShow,
  plans,
  filterOptions,
  filters,
  store,
  isMultiCarrier = false
) {
  if (!isMultiCarrier)
    renderFiltersSingleCarrier(plansToShow, plans, filterOptions, filters, store)
  else {
    renderFiltersMultiCarrier(plansToShow, plans, filterOptions)
  }
}

function renderFiltersSingleCarrier(plansToShow, plans, filterOptions, filters, store) {
  plansToShow.forEach(planId => {
    const plan = plans[planId]

    // coinsurance
    const coinsOpts = filterOptions[plan.planTypeKey].coinsurance.values.reduce(
      (result, current) => {
        result[current] = `${current}%`
        return result
      },
      {}
    )
    const coinsuranceAvailableOptions = getAvailableOptions({
      plans,
      plansSTM: store.plansSTM,
      currentPlan: plan,
      filters: [filterBenefits.deductible, filterBenefits.coverage, filterBenefits.moop],
      currentFilter: filterBenefits.coinsurance
    })
    setMultiselectOptions({
      options: coinsOpts,
      parentId: `coinsurance-${planId}-filter`,
      values: [plan.coreBenefits.userCoinsurance],
      availableOptions: coinsuranceAvailableOptions,
      name: `${planId}-coinsurance`,
      cssClass: 'js-option'
    })

    // coverage (lifetimeMax)
    const coverageOpts = filterOptions[plan.planTypeKey].coverage.values.reduce(
      (result, current) => {
        result[current] = numberToCurrency(current)
        return result
      },
      {}
    )
    const coverageAvailableOptions = getAvailableOptions({
      plans,
      plansSTM: store.plansSTM,
      currentPlan: plan,
      filters: [
        filterBenefits.coinsurance,
        filterBenefits.moop,
        filterBenefits.deductible
      ],
      currentFilter: filterBenefits.coverage
    })

    setMultiselectOptions({
      options: coverageOpts,
      parentId: `coverage-${planId}-filter`,
      values: [plan.coreBenefits.lifetimeMax],
      availableOptions: coverageAvailableOptions,
      name: `${planId}-coverage`,
      cssClass: 'js-option'
    })

    // deductible
    const deductibleOptions = getDeductibleOptions({
      plans,
      plan,
      plansSTM: store.plansSTM,
      filterOptions: filterOptions[plan.planTypeKey],
      benefits: filterBenefits
    })

    setMultiselectOptions({
      options: deductibleOptions.options,
      availableOptions: deductibleOptions.availableOptions,
      parentId: `deductible-${planId}-filter`,
      name: `${planId}-deductible`,
      values: [plan.coreBenefits.deductible],
      cssClass: 'js-option'
    })

    // moop
    const moopOpts = filterOptions[plan.planTypeKey].moop.values.reduce(
      (result, current) => {
        result[current] = numberToCurrency(current)
        return result
      },
      {}
    )
    const moopAvailableOptions = getAvailableOptions({
      plans,
      plansSTM: store.plansSTM,
      currentPlan: plan,
      filters: [
        filterBenefits.coinsurance,
        filterBenefits.deductible,
        filterBenefits.coverage
      ],
      currentFilter: filterBenefits.moop
    })

    setMultiselectOptions({
      options: moopOpts,
      parentId: `moop-${planId}-filter`,
      availableOptions: moopAvailableOptions,
      values: [plan.coreBenefits.outOfPocketMax],
      name: `${planId}-moop`,
      cssClass: 'js-option'
    })

    // period
    const showPeriodOptions =
      propAccessor(filterOptions, [plan.planTypeKey, 'period', 'values', 'length'], 0) > 1
    if (showPeriodOptions) {
      toggleClass(`#plan-card-${planId} .plan__feature-period`, 'd-flex', true)

      const periodOptions = getPeriodOptions({
        plan,
        filterOptions: filterOptions[plan.planTypeKey],
        filters: filters[plan.planTypeKey]
      })

      setMultiselectOptions({
        options: periodOptions.options,
        parentId: `period-${plan.planId}-filter`,
        name: `${planId}-period`,
        values: periodOptions.values,
        cssClass: 'js-option plan__extend-input'
      })
    }
  })
}

function renderFiltersMultiCarrier(plansToShow, plans, filterOptions) {
  const planId = plansToShow[0]
  const plan = plans[planId]

  const multiCarrierPlans = Object.entries(plans)
    .map(entry => entry[1])
    .filter(plan => plan?.multiCarrierPlan)

  // Get all the deductibles for multi-carrier plans
  const deductibles = multiCarrierPlans.map(plan => plan.coreBenefits.deductible)
  const dedupedDeductibles = deductibles.filter(
    (value, index) => deductibles.indexOf(value) === index && value
  )

  // Get all the max out of pockets for multi-carrier plans
  const moops = multiCarrierPlans.map(plan => plan.coreBenefits.outOfPocketMax)
  const dedupedMoops = moops.filter(
    (value, index) => moops.indexOf(value) === index && value
  )

  // Get all the coinsurances for multi-carrier plans
  const coinsuranceOptions = multiCarrierPlans.map(
    plan => plan.coreBenefits.userCoinsurance
  )
  const dedupedCoinsurances = coinsuranceOptions.filter(
    (value, index) => coinsuranceOptions.indexOf(value) === index && value
  )

  // Get all lifetime maximums for multi-carrier plans

  const lifetimeMaxiumums = multiCarrierPlans.map(plan => plan.coreBenefits.lifetimeMax)
  const dedupedMaximums = lifetimeMaxiumums.filter(
    (value, index) => lifetimeMaxiumums.indexOf(value) === index && value
  )

  filterOptions.multi = {
    coinsurance: {
      type: 'one',
      values: dedupedCoinsurances
    },
    coverage: {
      type: 'one',
      values: dedupedMaximums.map(max => parseInt(max))
    },
    deductible: {
      type: 'one',
      values: dedupedDeductibles.map(deductible => parseInt(deductible))
    },
    moop: {
      type: 'one',
      values: dedupedMoops.map(moop => parseInt(moop))
    }
  }

  const multiCarrierPlanIds = multiCarrierPlans.map(plan => plan.planId)

  // coinsurance
  const coinsOpts = filterOptions[plan.planTypeKey].coinsurance.values.reduce(
    (result, current) => {
      result[current] = `${current}%`
      return result
    },
    {}
  )
  const coinsuranceAvailableOptions = getAvailableOptions({
    plans,
    plansSTM: multiCarrierPlanIds,
    currentPlan: plan,
    filters: [filterBenefits.deductible, filterBenefits.coverage, filterBenefits.moop],
    currentFilter: filterBenefits.coinsurance
  })
  setMultiselectOptions({
    options: coinsOpts,
    parentId: `coinsurance-${planId}-filter`,
    values: [plan.coreBenefits.userCoinsurance],
    availableOptions: coinsuranceAvailableOptions,
    name: `${planId}-coinsurance`,
    cssClass: 'js-option'
  })

  // coverage (lifetimeMax)
  const coverageOpts = filterOptions[plan.planTypeKey].coverage.values.reduce(
    (result, current) => {
      result[current] = numberToCurrency(current)
      return result
    },
    {}
  )
  const coverageAvailableOptions = getAvailableOptions({
    plans,
    plansSTM: multiCarrierPlanIds,
    currentPlan: plan,
    filters: [filterBenefits.coinsurance, filterBenefits.moop, filterBenefits.deductible],
    currentFilter: filterBenefits.coverage
  })

  setMultiselectOptions({
    options: coverageOpts,
    parentId: `coverage-${planId}-filter`,
    values: [plan.coreBenefits.lifetimeMax],
    availableOptions: coverageAvailableOptions,
    name: `${planId}-coverage`,
    cssClass: 'js-option'
  })

  // deductible
  const deductibleOptions = getDeductibleOptions({
    plans,
    plan,
    plansSTM: multiCarrierPlanIds,
    filterOptions: filterOptions[plan.planTypeKey],
    benefits: filterBenefits
  })

  setMultiselectOptions({
    options: deductibleOptions.options,
    availableOptions: deductibleOptions.availableOptions,
    parentId: `deductible-${planId}-filter`,
    name: `${planId}-deductible`,
    values: [plan.coreBenefits.deductible],
    cssClass: 'js-option'
  })

  // moop
  const moopOpts = filterOptions[plan.planTypeKey].moop.values.reduce(
    (result, current) => {
      result[current] = numberToCurrency(current)
      return result
    },
    {}
  )
  const moopAvailableOptions = getAvailableOptions({
    plans,
    plansSTM: multiCarrierPlanIds,
    currentPlan: plan,
    filters: [
      filterBenefits.coinsurance,
      filterBenefits.deductible,
      filterBenefits.coverage
    ],
    currentFilter: filterBenefits.moop
  })
  // Remove the 10000 option if it exists for multi-carrier plans
  // This is a custom requirement based on the plan configuration
  if (moopOpts['10000']) {
    delete moopOpts['10000']
  }
  setMultiselectOptions({
    options: moopOpts,
    parentId: `moop-${planId}-filter`,
    availableOptions: moopAvailableOptions,
    values: [plan.coreBenefits.outOfPocketMax],
    name: `${planId}-moop`,
    cssClass: 'js-option'
  })
}

interface AvailableOptions {
  currentPlan: Plan
  currentFilter: string
  filters: string[]
  plansSTM: number[]
  plans: { [key: number]: Plan }
}

function getAvailableOptions({
  currentPlan,
  currentFilter,
  filters,
  plansSTM,
  plans
}: AvailableOptions) {
  const availableOptions = plansSTM
    .filter(planId => currentPlan.planTypeKey === plans[planId].planTypeKey)
    .filter(planId =>
      filters.reduce(
        (accum, currentValue) =>
          accum &&
          plans[planId].coreBenefits[currentValue] ===
            currentPlan.coreBenefits[currentValue],
        true
      )
    )
    .reduce((result, currentValue) => {
      return [...result, plans[currentValue].coreBenefits[currentFilter]]
    }, [])

  return availableOptions
}
export default renderPlanFilters
